import "../../styles/signupage.css";
import logo from "../../assets/images/logo.svg";
import { useState } from "react";
import {
  FaUserTie,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaMapMarkerAlt,
  FaMapPin,
  FaLanguage,
  FaIdCard,
  FaLock,
} from "react-icons/fa";
import { statesAndLGAs } from "../../assets/StatesAndLGs";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";

export const SignUpage = () => {
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    role: "",
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    state: "",
    lga: "",
    language: "",
    nin: "",
    password: "",
    confirmPassword: "",
    agreeTerms: false,
    otpPreference: "",
  });

  const [formErrors, setFormErrors] = useState({});
  const [lgas, setLgas] = useState([]);
  const [showLgaDropdown, setShowLgaDropdown] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRegistered, setIsRegistered] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (name === "state") {
      setLgas(statesAndLGAs[value] || []);
      setShowLgaDropdown(true);
      setFormData((prev) => ({ ...prev, lga: "" }));
    }
  };

  const validateForm = (data) => {
    const errors = {};

    if (!data.role.trim()) {
      errors.role = "Role is required";
    }
    if (!data.firstName.trim()) {
      errors.firstName = "First Name is required";
    }
    if (!data.lastName.trim()) {
      errors.lastName = "Last Name is required";
    }
    if (!data.email.trim()) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(data.email)) {
      errors.email = "Email is invalid";
    }
    if (!data.phoneNumber.trim()) {
      errors.phoneNumber = "Phone Number is required";
    } else if (!/^\+?\d{10,}$/.test(data.phoneNumber)) {
      errors.phoneNumber = "Phone Number is invalid";
    }
    if (!data.state.trim()) {
      errors.state = "State is required";
    }
    if (!data.lga.trim() && data.state) {
      errors.lga = "LGA is required";
    }
    if (!data.language.trim()) {
      errors.language = "Preferred Language is required";
    }
    if (!data.nin.trim()) {
      errors.nin = "NIN is required";
    } else if (!/^\d{11}$/.test(data.nin)) {
      errors.nin = "NIN must be 11 digits";
    }
    if (!data.password || data.password.length < 8) {
      errors.password = "Password must be at least 8 characters";
    } else if (!/[A-Z]/.test(data.password)) {
      errors.password = "Password must contain at least one uppercase letter";
    } else if (!/[0-9]/.test(data.password)) {
      errors.password = "Password must contain at least one number";
    }
    if (!data.confirmPassword || data.confirmPassword !== data.password) {
      errors.confirmPassword = "Passwords do not match";
    }
    if (!data.agreeTerms) {
      errors.agreeTerms = "You must agree to the terms and conditions";
    }
    if (!data.otpPreference.trim()) {
      errors.otpPreference = "OTP Preference is required";
    }
    return errors;
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateForm(formData);
    setFormErrors(errors);

    if (Object.keys(errors).length > 0) return;

    setIsSubmitting(true);

    try {
      // 1. Register the user
      const response = await axios.post(
        `${baseUrl}/api/v1/auth/register`,
        formData,
        { withCredentials: true }
      );
      console.log(response.data);
      if (!response.data.success) {
        throw new Error(response.data.message || "Registration failed");
      }

      // 2. Request OTP
      const otpResponse = await axios.post(
        `${baseUrl}/api/v1/auth/request-otp`,
        { email: formData.email, purpose: "registration-verification" },
        { withCredentials: true }
      );

      if (otpResponse.data.code === "SUCCESS") {
        setIsRegistered(true);

        setTimeout(() => {
          navigate("/verification", {
            state: {
              email: formData.email,
              message: "Registration successful! Please verify your email.",
            },
          });
        }, 2000);
      } else {
        throw new Error(otpResponse.data.message || "Failed to send OTP");
      }
    } catch (error) {
      console.error("Registration process failed:", error);
      setFormErrors({
        submit:
          error.response?.data?.message ||
          error.message ||
          "Registration failed. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render success message if registered
  if (isRegistered) {
    return (
      <div className="registration-success">
        <h2>Registration Successful!</h2>
        <p>We've sent a verification code to {formData.email}.</p>
        <p>Redirecting to verification page...</p>
      </div>
    );
  }

  return (
    <div className="main-container">
      <Link to="/">
        <div className="flex items-center space-x-2 pt-10">
          <img src={logo} alt="AgroLink" className="h-8" />
        </div>
      </Link>
      <div className="content2">
        <div className="content2div1">
          <span>Join AgroLink</span>
          <h4>
            Create your account to start connecting with the agricultural
            community
          </h4>
        </div>
        <div>
          <form onSubmit={handleSubmit} className="form-container" noValidate>
            {/* Role Selection */}
            <div className="form-group full-width">
              <label htmlFor="role" className="sr-only">
                Role
              </label>
              <div className="icon-input-wrapper">
                <FaUserTie className="input-icon" />
                <select
                  id="role"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                  required
                  className="icon-input"
                  aria-describedby="role-error"
                  disabled={isSubmitting}
                >
                  <option value="">Choose your role</option>
                  <option value="farmer">Farmer</option>
                  <option value="buyer/marketer">Buyer/Marketer</option>
                  <option value="transport agent">Transport Agent</option>
                </select>
              </div>
              {formErrors.role && (
                <span id="role-error" className="error">
                  {formErrors.role}
                </span>
              )}
            </div>

            {/* Two-column layout */}
            <div className="two-columns">
              {/* First Name */}
              <div className="form-group">
                <label htmlFor="firstName" className="sr-only">
                  First Name
                </label>
                <div className="icon-input-wrapper">
                  <FaUser className="input-icon" />
                  <input
                    id="firstName"
                    type="text"
                    name="firstName"
                    placeholder="First Name"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    className="icon-input"
                    aria-describedby="firstName-error"
                    disabled={isSubmitting}
                  />
                </div>
                {formErrors.firstName && (
                  <span id="firstName-error" className="error">
                    {formErrors.firstName}
                  </span>
                )}
              </div>

              {/* Last Name */}
              <div className="form-group">
                <label htmlFor="lastName" className="sr-only">
                  Last Name
                </label>
                <div className="icon-input-wrapper">
                  <FaUser className="input-icon" />
                  <input
                    id="lastName"
                    type="text"
                    name="lastName"
                    placeholder="Last Name"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    className="icon-input"
                    aria-describedby="lastName-error"
                    disabled={isSubmitting}
                  />
                </div>
                {formErrors.lastName && (
                  <span id="lastName-error" className="error">
                    {formErrors.lastName}
                  </span>
                )}
              </div>

              {/* Email */}
              <div className="form-group">
                <label htmlFor="email" className="sr-only">
                  Email
                </label>
                <div className="icon-input-wrapper">
                  <FaEnvelope className="input-icon" />
                  <input
                    id="email-signup"
                    type="email"
                    name="email"
                    placeholder="Email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="icon-input"
                    aria-describedby="email-error"
                    disabled={isSubmitting}
                  />
                </div>
                {formErrors.email && (
                  <span id="email-error" className="error">
                    {formErrors.email}
                  </span>
                )}
              </div>

              {/* Phone Number */}
              <div className="form-group">
                <label htmlFor="phoneNumber" className="sr-only">
                  Phone Number
                </label>
                <div className="icon-input-wrapper">
                  <FaPhone className="input-icon" />
                  <input
                    id="phoneNumber"
                    type="tel"
                    name="phoneNumber"
                    placeholder="Phone Number"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    required
                    className="icon-input"
                    aria-describedby="phoneNumber-error"
                    disabled={isSubmitting}
                  />
                </div>
                {formErrors.phoneNumber && (
                  <span id="phoneNumber-error" className="error">
                    {formErrors.phoneNumber}
                  </span>
                )}
              </div>

              {/* State */}
              <div className="form-group">
                <label htmlFor="state" className="sr-only">
                  State
                </label>
                <div className="icon-input-wrapper">
                  <FaMapMarkerAlt className="input-icon" />
                  <select
                    id="state"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    required
                    className="icon-input"
                    aria-describedby="state-error"
                    disabled={isSubmitting}
                  >
                    <option value="">Select State</option>
                    {Object.keys(statesAndLGAs).map((state) => (
                      <option key={state} value={state}>
                        {state}
                      </option>
                    ))}
                  </select>
                </div>
                {formErrors.state && (
                  <span id="state-error" className="error">
                    {formErrors.state}
                  </span>
                )}
              </div>

              {/* LGA */}
              {showLgaDropdown && (
                <div className="form-group">
                  <label htmlFor="lga" className="sr-only">
                    LGA
                  </label>
                  <div className="icon-input-wrapper">
                    <FaMapPin className="input-icon" />
                    <select
                      id="lga"
                      name="lga"
                      value={formData.lga}
                      onChange={handleChange}
                      required
                      className="icon-input"
                      aria-describedby="lga-error"
                      disabled={isSubmitting}
                    >
                      <option value="">Select LGA</option>
                      {lgas.map((lga) => (
                        <option key={lga} value={lga}>
                          {lga}
                        </option>
                      ))}
                    </select>
                  </div>
                  {formErrors.lga && (
                    <span id="lga-error" className="error">
                      {formErrors.lga}
                    </span>
                  )}
                </div>
              )}

              {/* Preferred Language */}
              <div className="form-group">
                <label htmlFor="language" className="sr-only">
                  Preferred Language
                </label>
                <div className="icon-input-wrapper">
                  <FaLanguage className="input-icon" />
                  <select
                    id="language"
                    name="language"
                    value={formData.language}
                    onChange={handleChange}
                    required
                    className="icon-input"
                    aria-describedby="language-error"
                    disabled={isSubmitting}
                  >
                    <option value="">Preferred Language</option>
                    <option value="english">English</option>
                    <option value="yoruba">Yoruba</option>
                    <option value="hausa">Hausa</option>
                    <option value="igbo">Igbo</option>
                    <option value="fulfulde">Fulfulde</option>
                    <option value="pidgin">Pidgin</option>
                    <option value="tiv">Tiv</option>
                    <option value="edo">Edo</option>
                    <option value="ibibio">Ibibio</option>
                    <option value="kanuri">Kanuri</option>
                  </select>
                </div>
                {formErrors.language && (
                  <span id="language-error" className="error">
                    {formErrors.language}
                  </span>
                )}
              </div>

              {/* NIN */}
              <div className="form-group">
                <label htmlFor="nin" className="sr-only">
                  NIN
                </label>
                <div className="icon-input-wrapper">
                  <FaIdCard className="input-icon" />
                  <input
                    id="nin"
                    type="text"
                    name="nin"
                    placeholder="NIN"
                    value={formData.nin}
                    onChange={handleChange}
                    required
                    className="icon-input"
                    aria-describedby="nin-error"
                    disabled={isSubmitting}
                  />
                </div>
                {formErrors.nin && (
                  <span id="nin-error" className="error">
                    {formErrors.nin}
                  </span>
                )}
              </div>

              {/* Password */}
              <div className="form-group">
                <label htmlFor="password" className="sr-only">
                  Password
                </label>
                <div className="icon-input-wrapper">
                  <FaLock className="input-icon" />
                  <input
                    id="password-signup"
                    type="password"
                    name="password"
                    placeholder="Password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    className="icon-input"
                    aria-describedby="password-error"
                    disabled={isSubmitting}
                  />
                </div>
                {formErrors.password && (
                  <span id="password-error" className="error">
                    {formErrors.password}
                  </span>
                )}
              </div>

              {/* Confirm Password */}
              <div className="form-group">
                <label htmlFor="confirmPassword" className="sr-only">
                  Confirm Password
                </label>
                <div className="icon-input-wrapper">
                  <FaLock className="input-icon" />
                  <input
                    id="confirmPassword"
                    type="password"
                    name="confirmPassword"
                    placeholder="Confirm Password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    className="icon-input"
                    aria-describedby="confirmPassword-error"
                    disabled={isSubmitting}
                  />
                </div>
                {formErrors.confirmPassword && (
                  <span id="confirmPassword-error" className="error">
                    {formErrors.confirmPassword}
                  </span>
                )}
              </div>
            </div>

            {/* OTP Preference - Radio */}
            <div className="form-group full-width">
              <label className="form-label">OTP Preference</label>
              <div className="radio-group">
                <label className="radio-label">
                  <input
                    type="radio"
                    name="otpPreference"
                    value="email"
                    checked={formData.otpPreference === "email"}
                    onChange={handleChange}
                    required
                  />
                  Email
                </label>
                {/* <label className="radio-label">
                  <input
                    type="radio"
                    name="otpPreference"
                    value="sms"
                    checked={formData.otpPreference === "sms"}
                    onChange={handleChange}
                    required
                  />
                  SMS
                </label> */}
              </div>
              {formErrors.otpPreference && (
                <span className="error">{formErrors.otpPreference}</span>
              )}
            </div>

            {/* Terms and Conditions */}
            <div className="terms full-width">
              <input
                type="checkbox"
                id="agreeTerms"
                name="agreeTerms"
                checked={formData.agreeTerms}
                onChange={handleChange}
                required
                aria-describedby="agreeTerms-error"
                disabled={isSubmitting}
              />
              <label htmlFor="agreeTerms">
                I agree to the Terms and Conditions
              </label>
              {formErrors.agreeTerms && (
                <span id="agreeTerms-error" className="error">
                  {formErrors.agreeTerms}
                </span>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="full-width"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </button>
            {formErrors.submit && (
              <span className="error">{formErrors.submit}</span>
            )}
            <p>
              Already have an account?{" "}
              <Link to="/signin" className="sign-in-link">
                Sign In Here
              </Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};
